import { Modal } from "antd";
import Toolbar from "ui/components/Toolbar";
import FormConfiguracaoRelatorio from "../FormConfiguracaoRelatorio";
import { useEffect, useState } from "react";
import useRelatorioPrestacaoContasItems from "core/features/relatorios/prestacao-contas/hooks/useRelatorioPrestacaoContasDetalhe";
import { useForm, SubmitErrorHandler } from "react-hook-form";
import {
  RelatorioPrestacaoContasForm,
  TipoItemRelatorioPrestacaoContas,
  CapaOrientacaoEnum,
  RelatorioPrestacaoContasItem,
} from "core/features/relatorios/prestacao-contas/typings";
import { useLoading } from "components/GlobalLoading";
import FooterModal from "components/Button/FooterModal";
import { showError } from "infra/helpers/alerta";
import useSaveRelatorioPrestacaoContas from "core/features/relatorios/prestacao-contas/hooks/useSaveRelatorioPrestacaoContas";
import { showSuccess } from "infra/helpers/alerta";
import { TipoFiltroDataRelatorioPrestacaoContas } from "features/relatorios/prestacao-contas/typings";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { getBlobAndAttachments } from "../../util/makeBlobOrAttachmentUrlList";
import moment from "moment";
import { Alert, AlertIcon, Checkbox } from "@chakra-ui/react";
import useDeletarItemRelatorioPrestacaoContas from "core/features/relatorios/prestacao-contas/hooks/useDeletarItemRelatorioPrestacaoContas";

interface ModalConfiguracaoRelatorioPrestacaoContasProps {
  isOpen: boolean;
  onClose: () => any;
  relatorioPrestacaoContasId?: string;
  refetch?: () => any;
  month?: string;
  mode?: "download" | "save";
  onDownload?: (
    form: RelatorioPrestacaoContasForm,
    incluirNumeroPaginas?: boolean
  ) => any;
}

function ModalConfiguracaoRelatorioPrestacaoContas({
  isOpen,
  onClose,
  relatorioPrestacaoContasId,
  refetch,
  month,
  mode = "save",
  onDownload,
}: ModalConfiguracaoRelatorioPrestacaoContasProps) {
  const { tenant } = useCurrentTenant();
  const { getData, isLoading } = useRelatorioPrestacaoContasItems();
  const [isLoadingPrevisualize, setIsLoadingPrevisualize] = useState(false);
  const { mutate: saveRelatorio, isLoading: isLoadingSave } =
    useSaveRelatorioPrestacaoContas();
  const { mutate: deletarItem, isLoading: isLoadingDeletarItem } =
    useDeletarItemRelatorioPrestacaoContas();
  const [incluirNumeroPaginas, setIncluirNumeroPaginas] = useState(true);

  const {
    control,
    setValue,
    reset,
    watch,
    handleSubmit,
    formState,
    trigger,
    unregister,
  } = useForm<RelatorioPrestacaoContasForm>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      tipoPeriodo: "sempre",
      dataInicio: undefined,
      dataFim: undefined,
      items: [
        {
          id: "1000",
          tipoItem: TipoItemRelatorioPrestacaoContas.Capa,
          orientacao: CapaOrientacaoEnum.Retrato,
          tipoFiltroData: TipoFiltroDataRelatorioPrestacaoContas.AnoAtual,
        },
      ],
    },
  });

  useEffect(() => {
    (async () => {
      if (relatorioPrestacaoContasId) {
        const form = await getData(relatorioPrestacaoContasId);

        const getTipoPeriodo: () => RelatorioPrestacaoContasForm["tipoPeriodo"] =
          () => {
            if (!form?.dataInicio) {
              return "sempre";
            }
            if (moment(form?.dataInicio).isSame(form?.dataFim, "month")) {
              return "mes";
            }
            return "periodo";
          };

        reset({ ...form, tipoPeriodo: getTipoPeriodo() });
      }
    })();
  }, [getData, relatorioPrestacaoContasId, reset]);

  useLoading(isLoading || isLoadingPrevisualize || isLoadingDeletarItem);

  const form = watch();

  const onSubmit = (_form: RelatorioPrestacaoContasForm) => {
    if (mode === "download") {
      return onDownload?.(form, incluirNumeroPaginas);
    }

    saveRelatorio(_form, {
      onSuccess: () => {
        showSuccess("Relatório salvo com sucesso").then(() => {
          onClose();
          refetch?.();
        });
      },
    });
  };

  const onInvalid: SubmitErrorHandler<RelatorioPrestacaoContasForm> = () => {
    trigger();
    showError("Algum dos itens está configurado de maneira incorreta.");
  };

  const onVisualize = async (item: Partial<RelatorioPrestacaoContasItem>) => {
    try {
      if (
        item.tipoItem === TipoItemRelatorioPrestacaoContas.PdfExterno &&
        item.arquivo
      ) {
        const _url = item.arquivo.referencia?.startsWith("https")
          ? item.arquivo.referencia
          : URL.createObjectURL(item.arquivo.referencia);

        window.open(_url, "_blank", "noopener noreferrer");
      } else {
        setIsLoadingPrevisualize(true);
        const result = await getBlobAndAttachments(
          { ...item, mes: month },
          tenant.imagemUrl
        );

        if (!result?.blob) {
          showError(
            "Não foi possível criar a previsualização do item. Tente atualizar a página."
          );
          return;
        }

        const _url = URL.createObjectURL(result?.blob);
        window.open(_url, "_blank", "noopener noreferrer");
      }
    } catch (err) {
      showError(
        "Não foi possível criar a previsualização do item. Tente atualizar a página."
      );
    } finally {
      setIsLoadingPrevisualize(false);
    }
  };

  const { errors } = formState;

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          isDisabled={isLoading || isLoadingSave}
          onSubmit={() => {
            handleSubmit(onSubmit, onInvalid)();
          }}
          primaryButtonText={mode === "download" ? "Baixar PDF" : undefined}
          showRegisterAnother={false}
          isLoadingSave={isLoadingSave}
          onClose={onClose}
        />
      }
      onCancel={onClose}
      destroyOnClose
      width="900px"
      bodyStyle={{
        height: "60vh",
        overflow: "auto",
        padding: 16,
        borderRadius: 16,
      }}
      className="antd-custom-header"
      title={
        <Toolbar
          title={
            mode === "download"
              ? "Baixar relatório"
              : "Configuração do relatório"
          }
          onClose={onClose}
          border="0px"
          alignItems="center"
        />
      }
      closable={false}
    >
      {!isLoading && mode === "download" ? (
        <Alert status="warning" mb={4} fontSize={12}>
          <AlertIcon />
          Apenas para revisão da configuração e alteração momentânea dos itens
          para download. As modificações não serão salvas para posteridade.
        </Alert>
      ) : null}
      {!isLoading && mode === "download" ? (
        <Checkbox
          isChecked={incluirNumeroPaginas}
          onChange={() => setIncluirNumeroPaginas((s) => !s)}
        >
          Incluir numeração das páginas
        </Checkbox>
      ) : null}
      {!isLoading ? (
        <FormConfiguracaoRelatorio
          showOnlyItems={mode === "download"}
          mode={mode}
          form={form}
          setValue={setValue}
          unregister={unregister}
          control={control}
          formErrors={errors}
          onVisualize={onVisualize}
          month={month}
          onDeletarItem={(itemId: string) =>
            relatorioPrestacaoContasId
              ? deletarItem({
                  id: relatorioPrestacaoContasId,
                  itemId,
                })
              : undefined
          }
        />
      ) : null}
    </Modal>
  );
}

export default ModalConfiguracaoRelatorioPrestacaoContas;
